import axiosInstance from '../helpers/api';

interface SavePropertyRequest {
  buyerId: string;
  propertyId: string;
  propertyDetails: {
    address: string;
    price: number;
    beds: number;
    baths: number;
    sqft?: number;
    images?: string[];
  };
  context?: {
    searchQuery?: string;
    matchScore?: number;
  };
}

interface BuyerActivity {
  id: string;
  buyerId: string;
  activityType: 'property_saved' | 'property_viewed' | 'showing_requested';
  propertyId: string;
  propertyData: any;
  createdAt: string;
}

class BuyerActivityService {
  private savedProperties: Set<string> = new Set();

  constructor() {
    // Load saved properties from localStorage on init
    const saved = localStorage.getItem('savedProperties');
    if (saved) {
      try {
        const savedArray = JSON.parse(saved);
        this.savedProperties = new Set(savedArray);
      } catch (e) {
        console.error('Failed to parse saved properties:', e);
      }
    }
  }

  /**
   * Save a property for the current buyer
   */
  async saveProperty(property: any): Promise<boolean> {
    try {
      // Get buyer context from localStorage
      const buyerContext = this.getBuyerContext();
      
      if (!buyerContext?.leadId) {
        console.error('No buyer context found');
        return false;
      }

      const request: SavePropertyRequest = {
        buyerId: buyerContext.leadId,
        propertyId: property.mlsNumber || property.id,
        propertyDetails: {
          address: property.address,
          price: property.price,
          beds: property.bedrooms || property.beds || 0,
          baths: property.bathrooms || property.baths || 0,
          sqft: property.square_feet || property.sqft,
          images: property.images
        },
        context: {
          searchQuery: sessionStorage.getItem('lastSearchQuery') || undefined,
          matchScore: property.matchScore
        }
      };

      // For now, save to localStorage (backend endpoint pending)
      // TODO: Replace with actual API call when backend is ready
      // const response = await axiosInstance.post('/api/portal/buyer-activity/save-property', request);

      // Temporary local storage implementation
      this.savedProperties.add(property.mlsNumber || property.id);
      localStorage.setItem('savedProperties', JSON.stringify(Array.from(this.savedProperties)));

      // Also store full property data for offline access
      const savedPropertiesData = JSON.parse(localStorage.getItem('savedPropertiesData') || '[]');
      savedPropertiesData.push({
        ...property,
        savedAt: new Date().toISOString(),
        buyerId: buyerContext.leadId
      });
      localStorage.setItem('savedPropertiesData', JSON.stringify(savedPropertiesData));

      return true;
    } catch (error) {
      console.error('Error saving property:', error);
      return false;
    }
  }

  /**
   * Remove a saved property
   */
  async unsaveProperty(propertyId: string): Promise<boolean> {
    try {
      this.savedProperties.delete(propertyId);
      localStorage.setItem('savedProperties', JSON.stringify(Array.from(this.savedProperties)));

      // Remove from saved data
      const savedPropertiesData = JSON.parse(localStorage.getItem('savedPropertiesData') || '[]');
      const filtered = savedPropertiesData.filter((p: any) => 
        p.mlsNumber !== propertyId && p.id !== propertyId
      );
      localStorage.setItem('savedPropertiesData', JSON.stringify(filtered));

      // TODO: Call backend API when ready
      // await axiosInstance.delete(`/api/portal/buyer-activity/saved-property/${propertyId}`);

      return true;
    } catch (error) {
      console.error('Error unsaving property:', error);
      return false;
    }
  }

  /**
   * Check if a property is saved
   */
  isPropertySaved(propertyId: string): boolean {
    return this.savedProperties.has(propertyId);
  }

  /**
   * Get all saved properties
   */
  getSavedProperties(): any[] {
    return JSON.parse(localStorage.getItem('savedPropertiesData') || '[]');
  }

  /**
   * Request a showing for a property
   */
  async requestShowing(propertyId: string, preferences: {
    preferredDates: string[];
    preferredTimes: string[];
    message?: string;
  }): Promise<boolean> {
    try {
      const buyerContext = this.getBuyerContext();
      
      if (!buyerContext?.leadId) {
        console.error('No buyer context found');
        return false;
      }

      // TODO: Implement backend API call
      // const response = await axiosInstance.post('/api/portal/buyer-activity/request-showing', {
      //   buyerId: buyerContext.leadId,
      //   propertyId,
      //   preferences
      // });

      // For now, store locally
      const showingRequests = JSON.parse(localStorage.getItem('showingRequests') || '[]');
      showingRequests.push({
        propertyId,
        buyerId: buyerContext.leadId,
        preferences,
        requestedAt: new Date().toISOString(),
        status: 'pending'
      });
      localStorage.setItem('showingRequests', JSON.stringify(showingRequests));

      return true;
    } catch (error) {
      console.error('Error requesting showing:', error);
      return false;
    }
  }

  /**
   * Get buyer context from localStorage
   */
  private getBuyerContext(): any {
    const portalContext = localStorage.getItem('portalBuyerContext');
    if (portalContext) {
      try {
        return JSON.parse(portalContext);
      } catch (e) {
        console.error('Failed to parse buyer context:', e);
      }
    }
    return null;
  }

  /**
   * Track property view (for analytics)
   */
  async trackPropertyView(propertyId: string): Promise<void> {
    try {
      const buyerContext = this.getBuyerContext();
      
      if (!buyerContext?.leadId) return;

      // TODO: Send to backend when ready
      // await axiosInstance.post('/api/portal/buyer-activity/property-view', {
      //   buyerId: buyerContext.leadId,
      //   propertyId,
      //   viewedAt: new Date().toISOString()
      // });

      // For now, track locally
      const viewHistory = JSON.parse(localStorage.getItem('propertyViewHistory') || '[]');
      viewHistory.push({
        propertyId,
        viewedAt: new Date().toISOString()
      });
      
      // Keep last 50 views
      if (viewHistory.length > 50) {
        viewHistory.shift();
      }
      
      localStorage.setItem('propertyViewHistory', JSON.stringify(viewHistory));
    } catch (error) {
      console.error('Error tracking property view:', error);
    }
  }
}

// Export singleton instance
export default new BuyerActivityService();